import { timeStringToSecondsSinceMidnight } from '@/helpers/dateHelper';
import { DefaultShiftTimes } from '@/services/settings/systemSettings.types';
import { useSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { strings } from '@/services/translation/strings';
import { useLocation } from '@/services/useLocations';
import { useMemo } from 'react';

const normaliseValue = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    if (value.includes(':')) return timeStringToSecondsSinceMidnight(value);
    const parsed = Number(value);
    if (!isNaN(parsed)) return parsed;
  }
  return 0;
};

const normaliseShiftTimes = (times: Record<string, any>): DefaultShiftTimes =>
  Object.fromEntries(
    Object.entries(times).map(([day, { start, end }]) => [
      day,
      {
        start: normaliseValue(start),
        end: normaliseValue(end)
      }
    ])
  ) as DefaultShiftTimes;

export const useLocationDefaultTimes = (locationId: number) => {
  const { location, fetching } = useLocation(locationId);
  const defaultTenantShiftTimes = useSystemSettings(s => s.settings.defaultShiftTimes);
  const defaultRosterMinStartTime = useSystemSettings(s => s.settings.minTime);
  const defaultRosterMaxEndTime = useSystemSettings(s => s.settings.maxTime);

  const { defaultShiftTimes, defaultRosterTimes, defaultOpeningTimes } = useMemo(() => {
    const properties = JSON.parse(location.properties ?? '{}');

    const defaultShiftTimes = normaliseShiftTimes(properties.defaultShiftTimes ?? defaultTenantShiftTimes);

    const defaultRosterTimes = normaliseShiftTimes(
      properties.defaultRosterTimes ??
        Object.fromEntries(
          strings.common.daysOfWeekFullStartMonday.map(day => [
            day.toLowerCase(),
            { start: defaultRosterMinStartTime, end: defaultRosterMaxEndTime }
          ])
        )
    );

    const defaultOpeningTimes = normaliseShiftTimes(properties.defaultOpeningTimes ?? defaultTenantShiftTimes);

    return { defaultShiftTimes, defaultRosterTimes, defaultOpeningTimes };
  }, [location.properties, defaultTenantShiftTimes, defaultRosterMinStartTime, defaultRosterMaxEndTime]);

  return { fetching, defaultShiftTimes, defaultRosterTimes, defaultOpeningTimes };
};
